import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { Icon } from '@components/icons';

const blogs = [
  {
    title: 'More Than Just Key Strokes: The Reality of Programming and Hacking!',
    description:
      'Programming and ethical hacking are more than just typing code—they involve creativity, critical thinking, planning, and continuous learning. This blog uncovers the deeper realities behind the craft.',
    tech: ['Programming Insights', 'Ethical Hacking', 'Developer Mindset', 'Reality of Coding'],
    external:
      'https://medium.com/@noorulbaseer/more-than-just-key-strokes-the-reality-of-programming-and-hacking-54304b2b20f0',
  },
];

const StyledBlogsSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;

  .numbered-heading {
    margin-bottom: 20px !important;
  }

  @media (min-width: 769px) {
    padding: 0 0 100px 0 !important;
  }

  /* Using global .numbered-heading styles */

  .blogs-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 20px;
    width: 100%;

    @media (max-width: 1080px) {
      grid-template-columns: 1fr;
    }
  }

  .more-button {
    ${({ theme }) => theme.mixins.bigButton};
    margin: 80px auto 0;
    border-radius: var(--border-radius);
  }

  @media (max-width: 768px) {
    min-height: 100vh !important;
    padding-bottom: 100px !important;
  }

  @media (max-width: 1080px) and (orientation: landscape), (max-height: 600px) {
    min-height: auto !important;
  }
`;

const StyledBlogCard = styled.li`
  position: relative;
  transition: var(--transition);
  max-width: 400px;
  width: 100%;
  justify-self: center;

  .blog-inner {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2.5rem 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 42, 42, 0.85);
      box-shadow: 0 0 30px 2px rgba(255, 42, 42, 0.35), 0 20px 40px -15px rgba(0, 0, 0, 0.6);

      .folder svg {
        transform: scale(1.1);
      }

      .blog-title {
        color: #ff2a2a;
        a {
          color: #ff2a2a;
        }
      }
    }
  }

  .blog-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: 30px;

    .folder {
      color: var(--green);
      svg {
        width: 45px;
        height: 45px;
        transition: var(--transition);
      }
    }

    .blog-links {
      display: flex;
      align-items: center;

      a {
        padding: 5px;
        color: var(--light-slate);
        transition: var(--transition);

        &:hover {
          color: var(--green);
        }

        svg {
          width: 22px;
          height: 22px;
        }
      }
    }
  }

  .blog-title {
    margin: 0 0 15px;
    color: var(--white);
    font-size: var(--fz-xxl);
    font-weight: 600;
    transition: none;

    a {
      transition: none;
      &::after {
        display: none;
      }
    }
  }

  .blog-description {
    color: var(--light-slate);
    font-size: 16px;
    line-height: 1.6;
    flex-grow: 1;
  }

  .blog-tech-list {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;
    gap: 8px;

    li {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 4px 10px;
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 12px;
      color: var(--lightest-slate);
      transition: all 0.3s ease;

      &:hover {
        background: #ff2a2a;
        color: #08080c !important;
        border-color: #ff2a2a;
        transform: scale(1.1);
        cursor: pointer;
      }
    }
  }
`;

const Blogs = () => {
  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealBlogs = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    revealBlogs.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion]);

  const GRID_LIMIT = 6;
  const firstSix = blogs.slice(0, GRID_LIMIT);
  const publishedBlogs = showMore ? blogs : firstSix;

  const blogInner = node => {
    const { title, description, tech, external } = node;

    return (
      <div className="blog-inner">
        <header>
          <div className="blog-top">
            <div className="folder">
              <Icon name="Article" />
            </div>
            <div className="blog-links">
              {external && (
                <a href={external} aria-label="External Link" target="_blank" rel="noreferrer">
                  <Icon name="External" />
                </a>
              )}
            </div>
          </div>
          <h3 className="blog-title">
            <a href={external} target="_blank" rel="noreferrer">
              {title}
            </a>
          </h3>
          <div className="blog-description" dangerouslySetInnerHTML={{ __html: description }} />
        </header>
        <footer>
          {tech && (
            <ul className="blog-tech-list">
              {tech.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledBlogsSection id="blogs">
      <h2 className="numbered-heading" ref={revealTitle}>
        Recent Articles
      </h2>

      <ul className="blogs-grid">
        {prefersReducedMotion ? (
          <>
            {publishedBlogs.map((node, i) => (
              <StyledBlogCard key={i}>{blogInner(node)}</StyledBlogCard>
            ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {publishedBlogs.map((node, i) => (
              <CSSTransition
                key={i}
                classNames="fadeup"
                timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                exit={false}
              >
                <StyledBlogCard
                  key={i}
                  ref={el => (revealBlogs.current[i] = el)}
                  style={{ transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms` }}
                >
                  {blogInner(node)}
                </StyledBlogCard>
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
      </ul>

      {blogs.length > GRID_LIMIT && (
        <button className="more-button" onClick={() => setShowMore(!showMore)}>
          Show {showMore ? 'Less' : 'More'}
        </button>
      )}
    </StyledBlogsSection>
  );
};

export default Blogs;
